import React, { createContext, useContext, useState, useEffect } from 'react';
import { addDays, isAfter, isBefore, isEqual } from 'date-fns';

interface Booking {
  id: string;
  guestName: string;
  email: string;
  phone: string;
  checkIn: Date;
  checkOut: Date;
  guests: number;
  totalPrice: number;
  status: 'pending' | 'confirmed' | 'cancelled';
  createdAt: Date;
  extras?: {
    breakfast?: boolean;
    airportPickup?: boolean;
  };
}

interface BookingContextType {
  bookings: Booking[];
  blockedDates: Date[];
  createBooking: (booking: Omit<Booking, 'id' | 'createdAt' | 'status'>) => void;
  updateBookingStatus: (id: string, status: Booking['status']) => void;
  isDateAvailable: (date: Date) => boolean;
  getBookingPrice: (checkIn: Date, checkOut: Date, guests: number) => number;
  addBlockedDate: (date: Date) => void;
  removeBlockedDate: (date: Date) => void;
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

const DAILY_RATE = 150; // Base rate per night
const GUEST_RATE = 25; // Additional rate per guest beyond 2

export const BookingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [blockedDates, setBlockedDates] = useState<Date[]>([]);

  useEffect(() => {
    // Load bookings from localStorage
    const savedBookings = localStorage.getItem('villa-bookings');
    if (savedBookings) {
      const parsed = JSON.parse(savedBookings);
      setBookings(parsed.map((b: any) => ({
        ...b,
        checkIn: new Date(b.checkIn),
        checkOut: new Date(b.checkOut),
        createdAt: new Date(b.createdAt),
      })));
    }

    // Load blocked dates
    const savedBlocked = localStorage.getItem('villa-blocked-dates');
    if (savedBlocked) {
      setBlockedDates(JSON.parse(savedBlocked).map((d: string) => new Date(d)));
    }
  }, []);

  const createBooking = (bookingData: Omit<Booking, 'id' | 'createdAt' | 'status'>) => {
    const newBooking: Booking = {
      ...bookingData,
      id: Date.now().toString(),
      status: 'pending',
      createdAt: new Date(),
    };
    
    const updatedBookings = [...bookings, newBooking];
    setBookings(updatedBookings);
    localStorage.setItem('villa-bookings', JSON.stringify(updatedBookings));
  };

  const updateBookingStatus = (id: string, status: Booking['status']) => {
    const updatedBookings = bookings.map(booking =>
      booking.id === id ? { ...booking, status } : booking
    );
    setBookings(updatedBookings);
    localStorage.setItem('villa-bookings', JSON.stringify(updatedBookings));
  };

  const isDateAvailable = (date: Date): boolean => {
    // Check if date is blocked
    if (blockedDates.some(blocked => isEqual(blocked, date))) {
      return false;
    }

    // Check if date is within any confirmed booking
    return !bookings.some(booking => 
      booking.status === 'confirmed' &&
      (isEqual(date, booking.checkIn) || 
       isEqual(date, booking.checkOut) ||
       (isAfter(date, booking.checkIn) && isBefore(date, booking.checkOut)))
    );
  };

  const getBookingPrice = (checkIn: Date, checkOut: Date, guests: number): number => {
    const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
    const basePrice = nights * DAILY_RATE;
    const extraGuestFee = guests > 2 ? (guests - 2) * GUEST_RATE * nights : 0;
    return basePrice + extraGuestFee;
  };

  const addBlockedDate = (date: Date) => {
    const updatedBlocked = [...blockedDates, date];
    setBlockedDates(updatedBlocked);
    localStorage.setItem('villa-blocked-dates', JSON.stringify(updatedBlocked));
  };

  const removeBlockedDate = (date: Date) => {
    const updatedBlocked = blockedDates.filter(blocked => !isEqual(blocked, date));
    setBlockedDates(updatedBlocked);
    localStorage.setItem('villa-blocked-dates', JSON.stringify(updatedBlocked));
  };

  return (
    <BookingContext.Provider value={{
      bookings,
      blockedDates,
      createBooking,
      updateBookingStatus,
      isDateAvailable,
      getBookingPrice,
      addBlockedDate,
      removeBlockedDate,
    }}>
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};